import { Fragment } from 'react';
import { useParams } from 'react-router';
import type { FeedbackChange, FeedbackDraft, FeedbackThread } from '@proofroom/schema';
import { Header } from '../components/Header';
import { Chip, EmptyState, ErrorState, Spinner, formatDate } from '../components/ui';
import { documents } from '../api/endpoints';
import { useFeedback } from '../api/queries';
import { wordDiff } from '../model/wordDiff';

const UNPAGED = 'No page';

/** Threads by page, pages in numeric order, the ones with no page recorded last. */
function groupByPage(threads: FeedbackThread[]): Array<[string, FeedbackThread[]]> {
  const groups = new Map<string, FeedbackThread[]>();
  for (const t of threads) {
    const key = t.page ? `Page ${t.page}` : UNPAGED;
    groups.set(key, [...(groups.get(key) ?? []), t]);
  }
  return [...groups.entries()].sort(([a], [b]) => {
    if (a === UNPAGED) return 1;
    if (b === UNPAGED) return -1;
    return a.localeCompare(b, undefined, { numeric: true });
  });
}

function Thread({ t }: { t: FeedbackThread }) {
  return (
    <article className="feedback-thread" aria-label={`Thread at ${t.location || t.id}`}>
      <div className="head">
        <Chip kind={t.status}>{t.status}</Chip>
        {t.location && <span className="quote">{t.location}</span>}
      </div>
      <ol>
        {t.comments.map((c, i) => (
          <li key={i}>
            <span className="who">{c.author.name}</span>
            <Chip kind="role">{c.author.role}</Chip>
            <span className="when"> {formatDate(c.createdAt)}</span>
            <p>{c.body}</p>
          </li>
        ))}
      </ol>
      {t.attachments.length > 0 && <div className="meta">Attachments: {t.attachments.join(', ')}</div>}
      {t.resolvedBy && <div className="meta">Resolved by {t.resolvedBy.name}, {formatDate(t.resolvedAt)}</div>}
    </article>
  );
}

function Change({ c }: { c: FeedbackChange }) {
  return (
    <article className="feedback-change" aria-label={`Change to ${c.paraId ?? c.storyId}`}>
      <div className="head">
        <Chip kind={c.status}>{c.status}</Chip>
        <span>{c.paraId ?? c.storyId}</span>
        <span className="who">{c.author.name}</span>
        <Chip kind="role">{c.author.role}</Chip>
        <span>{formatDate(c.createdAt)}</span>
      </div>
      <p className="inline-diff">
        {wordDiff(c.before, c.after).map((seg, i) => {
          if (seg.kind === 'removed') return <del key={i}>{seg.text}</del>;
          if (seg.kind === 'added') return <ins key={i}>{seg.text}</ins>;
          return <Fragment key={i}>{seg.text}</Fragment>;
        })}
      </p>
      {c.decidedBy && <div className="meta">Decided by {c.decidedBy.name}, {formatDate(c.decidedAt)}</div>}
    </article>
  );
}

function DraftSection({ d }: { d: FeedbackDraft }) {
  const headingId = `feedback-draft-${d.draft.id}`;
  const empty = d.threads.length === 0 && d.changes.length === 0;
  return (
    <section className="card feedback-draft" aria-labelledby={headingId}>
      <h2 id={headingId}>Draft {d.draft.number}{d.draft.label ? `: ${d.draft.label}` : ''}</h2>
      <div className="meta">Created {formatDate(d.draft.createdAt)}</div>
      {empty && <p className="muted">No feedback on this draft.</p>}
      {groupByPage(d.threads).map(([page, threads]) => (
        <Fragment key={page}>
          <h3>{page}</h3>
          {threads.map((t) => <Thread key={t.id} t={t} />)}
        </Fragment>
      ))}
      {d.changes.length > 0 && (
        <>
          <h3>Changes</h3>
          {d.changes.map((c) => <Change key={c.id} c={c} />)}
        </>
      )}
    </section>
  );
}

/** The staff report: everything said about a document, draft by draft, laid out to be printed or saved as a CSV. */
export function Feedback() {
  const { documentId = '' } = useParams();
  const q = useFeedback(documentId);
  const out = q.data;
  return (
    <div className="app feedback">
      <Header crumbs={[{ label: 'Projects', to: '/projects' }, { label: out?.document.name ?? 'Document', to: `/documents/${documentId}` }, { label: 'Feedback' }]} />
      <main className="main">
        <div className="page feedback-report">
          <div className="page-head">
            <div>
              <h1>Feedback on {out?.document.name ?? 'this document'}</h1>
              {out && <span className="sub">{out.project.name} · Generated {formatDate(out.generatedAt)}</span>}
            </div>
            <div className="btn-row no-print">
              <a className="btn" href={documents.feedbackCsvUrl(documentId)} download>Download CSV</a>
              <button className="btn primary" onClick={() => window.print()}>Print</button>
            </div>
          </div>
          {q.isPending && <Spinner label="Collecting feedback" />}
          {q.isError && <ErrorState title="Could not load the feedback" error={q.error} retry={() => void q.refetch()} />}
          {out && out.drafts.length === 0 && <EmptyState title="No drafts yet">Nothing has been published, so there is nothing to report.</EmptyState>}
          {out?.drafts.map((d) => <DraftSection key={d.draft.id} d={d} />)}
        </div>
      </main>
    </div>
  );
}
